import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DataStudioHelperService } from 'app/data-studio-redesign/services/data-studio-helper.service';
import { DataStudioRedesignService } from 'app/shared/services/data-services/data-studio-redesign/data-studio-redesign.service';
import { DataStudioFileType } from 'app/shared/services/data-services/graphql';
import { combineLatest, of, Subject, Subscription } from 'rxjs';
import { catchError, filter, switchMap, takeUntil } from 'rxjs/operators';
import { ThemeService } from '@wind-log_library/fe-component-library';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { TimezoneService } from 'app/shared/services/timezone/timezone.service';
import { DEVICE_TYPES } from 'app/data-studio-redesign/interfaces/data-studio.interface';

@Component({
  selector: 'app-data-file-summary',
  templateUrl: './data-file-summary.component.html',
  styleUrls: ['./data-file-summary.component.scss'],
  standalone: false
})
export class DataFileSummaryComponent implements OnInit, OnDestroy {

  @Input() displayPreviewButton = true;
  @Input() fileFormat = 'all';
  @Input() selectedPage = 1;
  public numberOfRows = 0;
  public numberOfFields = 0;
  public fileSize = 0;
  public numberOfFieldsLimit = 25000000;
  public waitingTime = 1;
  public enumFileType = DataStudioFileType;
  public fileType: DataStudioFileType;
  public dataStudioFileRquest;
  public requestedFile = false;
  private unsubscribe$ = new Subject();
  public fileName = moment().format('ddd DD MMM YYYY HH:mm:ss');
  public theme$;
  public tsSubscription;
  public tsFiles = [];
  public wrongName = false;
  public nameToPopUp;
  public dateDifferenceInDays;
  public columnLayout: DataStudioColumnLayout;
  public enumColumnLayoutType = DataStudioColumnLayoutType;
  private customFileName: boolean = false;

  @Output() isPreviewSelectedChange: EventEmitter<boolean> = new EventEmitter();

  @Input() isPreviewSelected = false;
  @Input() isPreviewButtonAvailable = false;
  constructor(private dataStudioHelper: DataStudioHelperService,
    public dataStudioService: DataStudioRedesignService,
    private themeService: ThemeService,
    private timezoneService: TimezoneService
  ) {
  }

  get xlsButtonDisabled() {
    return (![1, 2].includes(this.selectedPage) && this.dataStudioService.requestingSignalsData) ||
      this.dataStudioFileRquest?.signals.length <= 0 ||
      (this.selectedPage !== 4 && (this.dataStudioFileRquest?.turbines <= 0 || this.dataStudioFileRquest?.granularity === '')) ||
      this.fileName === '' ||
      this.wrongName ||
      this.dataStudioFileRquest?.wrongDate ||
      this.numberOfFields > this.numberOfFieldsLimit

  }

  get previewDisabled() {
    const property = this.isTurbine ? 'turbines' : 'deviceIds'
    return this.dataStudioService.requestingSignalsData ||
      this.dataStudioFileRquest?.signals.length <= 0 ||
      (this.selectedPage !== 4 && this.dataStudioFileRquest?.[property] <= 0) ||
      (this.selectedPage === 2 && this.dateDifferenceInDays > 7) ||
      (this.selectedPage === 1 && this.dateDifferenceInDays > 90);
  }

  get isTurbine() {
    return this.dataStudioHelper.typeSelection === DEVICE_TYPES.TURBINE;
  }

  get isMetmast() {
    return this.dataStudioHelper.typeSelection === DEVICE_TYPES.METMAST;
  }

  get isControlSystem() {
    return this.dataStudioHelper.typeSelection === DEVICE_TYPES.CONTROL_SYSTEM;
  }

  ngOnInit() {
    this.themeService.getTheme().pipe(takeUntil(this.unsubscribe$)).subscribe(theme => {
      this.theme$ = theme;
    });
    this.fileType = this.enumFileType.Xlsx;
    this.columnLayout = {
      type: this.enumColumnLayoutType.wide,
      oneTab: false
    };
    combineLatest([this.dataStudioHelper.getDateObservable$(), this.timezoneService.getTimezoneObservable()])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([dateData]) => {
        this.setData(dateData);
    });

    combineLatest([this.dataStudioHelper.getDateObservableLiveData$(), this.timezoneService.getTimezoneObservable()])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([dateData]) => {
        this.setData(dateData);
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.unsubscribe();
  }

  public setFileType(event) {
    this.fileType = event.value;
  }

  private requestMetaDataFile() {
    this.dataStudioService.getMetadataFile(this.dataStudioFileRquest, this.fileName);
  }

  private getOneTabValue(): boolean {
    // Csv files always have one tab, so we return true for them
    if (this.fileType === this.enumFileType.Csv) { 
      return true;
    }
    return this.columnLayout.oneTab;
  }

  private getLongFormatValue(): boolean {
    // Turbine logs does not have formats when multitab is selected, wide by default
    if (this.selectedPage === 3 && !this.getOneTabValue()) {
        return false;
    }
    return this.columnLayout.type === this.enumColumnLayoutType.long;
  }

  public requestDataFile() {
    this.requestedFile = true;
    this.dataStudioFileRquest.fileType = this.fileType;
    this.dataStudioFileRquest.fileName = this.fileName;
    this.dataStudioFileRquest.isNew = true;
    this.dataStudioFileRquest.longFormat = this.getLongFormatValue();
    this.dataStudioFileRquest.oneTab = this.getOneTabValue();
    this.dataStudioFileRquest.deviceType = this.isControlSystem ? 'controlSystem' : this.isMetmast ? 'metmastSensor' : 'turbine';
    this.dataStudioFileRquest.turbines = this.isTurbine ? this.dataStudioFileRquest.turbines : undefined,
    this.dataStudioFileRquest.granularity = this.isTurbine ? this.dataStudioFileRquest.granularity : 'live',
    this.dataStudioFileRquest.deviceIds = this.isTurbine ? this.dataStudioFileRquest.turbines : this.dataStudioFileRquest.deviceIds;
    if (this.selectedPage === 5) {
      this.requestMetaDataFile();
      return;
    }
    let fileRequestStatusSubscription: Subscription = null;
    const isScadaOrLiveData = ['scada', 'live'].includes(this.dataStudioFileRquest.queryType);
    if (isScadaOrLiveData) {
      fileRequestStatusSubscription = this.dataStudioService.getRequestNew(this.dataStudioFileRquest)
      .pipe(
        catchError(e => of(null)),
        filter(data => !!data),
        switchMap((data: any) => this.dataStudioService.getFileRequestStatus(data?.trackerId))
      )
      .subscribe(response => {
        const fileData = response;
        if (fileData.status === 'SUCCESSFUL') {
          this.dataStudioService.getDownloadFileNew(response.link).subscribe(response => {
          const dataType: any = response.type;
          const binaryData = [];
          binaryData.push(response);
          const blob = new Blob(binaryData, { type: dataType });
          saveAs(blob, `${fileData.fileName}`);
          if (response) {
            this.dataStudioHelper.setDateObservable$({clear: 'clear', fileName: this.nameToPopUp});
            this.requestedFile = false;
            this.nameToPopUp = this.fileName;
            this.fileName = '';
            this.numberOfRows = this.numberOfFields = this.fileSize = 0;
          }
        }, ( error => {}), () => {
          fileRequestStatusSubscription.unsubscribe();
          this.dataStudioService.isRequesting$.next(false);
        });
        }
      })
    } else {
      this.dataStudioService.getRequestFile(this.dataStudioFileRquest)
      .pipe(catchError(e => {
          return of(null);
      }))
      .subscribe(response => {
        if (response) {
          const dataType: any = response.type;
          const binaryData = [];
  
          if (dataType === 'text/csv') {
            const bom = '\uFEFF';
            // add BOM at the beginning of the file
            binaryData.push(bom, response);
          } else binaryData.push(response);
  
          const blob = new Blob(binaryData, { type: dataType });
          saveAs(blob, `${this.dataStudioFileRquest.fileName}.${this.dataStudioFileRquest.fileType}`);
          this.nameToPopUp = this.fileName;
          this.fileName = '';
          this.numberOfRows = this.numberOfFields = this.fileSize = 0;
          setTimeout(() => {
            this.requestedFile = false;
          }, 2000);
          setTimeout(() => {
            this.dataStudioHelper.setDateObservable$({clear: 'clear', fileName: this.nameToPopUp});
          }, 100);
        }
      }, ( error => {}), () => {
        this.dataStudioService.isRequesting$.next(false);
      });
    }
  }

  public testNameKeyPress(event) {
    const txt = String.fromCharCode(event.which);
    if (!txt.match(/[A-Za-z0-9_-]/)) {
      this.dataStudioFileRquest.fileName = '';
      return false;
    } else {
      this.dataStudioFileRquest.fileName = this.fileName;
    }
    this.customFileName = true;

  }

  public setColumnLayout(event) {
    this.columnLayout.type = event.value;
  }

  public setOneTab(event) {
    this.columnLayout.oneTab = event.checked;
  }

  showPreview() {
    this.isPreviewSelected = !this.isPreviewSelected;
    this.isPreviewSelectedChange.emit(this.isPreviewSelected);
  }

  private setData(dateData) {
    const ajustedDate = this.timezoneService.unApplyTzFromDate(dateData.dateTo).valueOf();
    if(!this.customFileName || this.fileName === '') {
      this.fileName = moment(ajustedDate).format('ddd DD MMM YYYY HH:mm:ss');
    }
    const dateDiff = moment(dateData.dateTo).diff(moment(dateData.dateFrom));
    this.dateDifferenceInDays = Math.ceil(moment.duration(dateDiff).asDays());
    if (dateData.estimatedRows > 0 || dateData.estimatedFields > 0 || dateData.estimatedFileSize > 0) {
      this.numberOfRows = dateData.estimatedRows;
      this.numberOfFields = dateData.estimatedFields;
      this.fileSize = dateData.estimatedFileSize;
      this.dataStudioFileRquest = JSON.parse(JSON.stringify(dateData.fileRequest));
      this.dataStudioFileRquest.fileType = this.fileType;
      this.dataStudioFileRquest.park = dateData.assetPark;
    }
  }
}

export enum DataStudioColumnLayoutType {
  long = 'long',
  wide = 'wide',
}
export interface DataStudioColumnLayout { 
  type: DataStudioColumnLayoutType,
  oneTab: boolean
}
